/**
 * GoalStepsInline — compact 2-column steps grid for goal cards.
 * Interactive: owner can toggle steps directly from card view.
 * Shows max 6 steps; surplus shown as "+N more" label.
 */

import { useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import type { GoalStep, GoalEntry } from '@turnifly/shared/types'
import { useToggleStep } from '@turnifly/shared/hooks'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'

const MAX_VISIBLE = 6

interface GoalStepsInlineProps {
  steps: GoalStep[]
  entries: GoalEntry[]
  goalId: string
  isOwner: boolean
}

export function GoalStepsInline({
  steps,
  entries,
  goalId,
  isOwner,
}: GoalStepsInlineProps): React.ReactElement {
  const { t } = useTranslation()
  const toggleStep = useToggleStep()

  const completedSet = useMemo((): Set<number> => {
    const set = new Set<number>()
    for (const e of entries) {
      if (e.step_index !== null) set.add(e.step_index)
    }
    return set
  }, [entries])

  const handleToggle = useCallback((index: number, label: string): void => {
    if (!isOwner || toggleStep.isPending) return
    hapticImpact('light')
    toggleStep.mutate({
      goalId,
      stepIndex: index,
      label,
      isCompleting: !completedSet.has(index),
    })
  }, [isOwner, goalId, completedSet, toggleStep])

  if (steps.length === 0) return <></>

  const visible = steps.slice(0, MAX_VISIBLE)
  const hiddenCount = steps.length - visible.length

  return (
    <div className="mt-2.5">
      <div className="grid grid-cols-2 gap-1.5">
        {visible.map((step, index) => {
          const isDone = completedSet.has(index)
          return (
            <motion.button
              key={index}
              type="button"
              whileTap={isOwner ? { scale: 0.97 } : undefined}
              onClick={() => handleToggle(index, step.label)}
              disabled={!isOwner || toggleStep.isPending}
              className={`flex items-center gap-2 px-2.5 py-2 rounded-xl text-left transition-all tap-highlight-transparent ${
                isDone
                  ? 'bg-success/[0.07] border border-success/[0.15]'
                  : 'bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.07]'
              } ${!isOwner ? 'cursor-default' : ''}`}
            >
              {/* Checkbox */}
              <div className={`w-[14px] h-[14px] shrink-0 rounded-[4px] border flex items-center justify-center transition-all ${
                isDone
                  ? 'bg-success border-success'
                  : 'border-white/[0.18] bg-transparent'
              }`}>
                {isDone && (
                  <svg viewBox="0 0 16 16" className="w-2.5 h-2.5 text-white">
                    <path
                      d="M3 8l3 3 7-7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>

              {/* Label */}
              <span className={`text-[11px] font-medium leading-tight truncate ${
                isDone ? 'text-text-hint line-through' : 'text-text'
              }`}>
                {step.label}
              </span>
            </motion.button>
          )
        })}
      </div>

      {hiddenCount > 0 && (
        <p className="mt-2 text-center text-[10px] text-text-hint/50">
          +{hiddenCount} {t('goals.moreSteps')}
        </p>
      )}
    </div>
  )
}
